// src/components/WelcomeOverview.js
import React from 'react';
import { useAppContext } from '../context/AppContext';

function WelcomeOverview({ onClose }) {
  const { setCurrentView } = useAppContext();

  const features = [
    {
      icon: '💪',
      title: 'Custom Workouts',
      description: 'Generate personalized Pilates routines based on your preferences, equipment, and fitness goals',
      action: () => {
        setCurrentView('generator');
        onClose();
      }
    },
    {
      icon: '🧠',
      title: 'Smart Modifications',
      description: 'Get exercise modifications based on your health considerations and individual needs',
      action: () => {
        setCurrentView('generator');
        onClose();
      }
    },
    {
      icon: '📊',
      title: 'Track Progress',
      description: 'Monitor your workout history and see your improvements over time with detailed analytics',
      action: () => {
        setCurrentView('progress');
        onClose();
      }
    },
    {
      icon: '❤️',
      title: 'Save Favorites',
      description: 'Bookmark your favorite exercises and workouts for quick access later',
      action: () => {
        setCurrentView('favorites');
        onClose();
      }
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-16 text-white text-center">
        <h1 className="text-4xl font-bold mb-4">Welcome to PilatesPlanner</h1>
        <p className="text-blue-100 max-w-2xl mx-auto text-lg">
          Your personal Pilates assistant that helps you create custom workouts, 
          track your progress, and reach your fitness goals.
        </p>
      </div>

      <div className="p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Here's what you can do with PilatesPlanner</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="border border-gray-200 rounded-lg p-6 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all group"
              onClick={feature.action}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 h-12 w-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-2xl mr-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center">
          <button
            onClick={() => {
              setCurrentView('generator');
              onClose();
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg shadow-sm transition-colors group"
          >
            <span className="flex items-center">
              <span className="mr-2 group-hover:scale-110 transition-transform">✨</span>
              Create Your First Workout
            </span>
          </button>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-center">
            <div className="flex items-center">
              <span className="text-blue-500 mr-2">📱</span>
              <span className="text-gray-700">Works on all devices</span>
            </div>
            <div className="flex items-center">
              <span className="text-blue-500 mr-2">🔒</span>
              <span className="text-gray-700">Free account to save progress</span>
            </div>
            <div className="flex items-center">
              <span className="text-blue-500 mr-2">⭐</span>
              <span className="text-gray-700">Pro features available</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 px-6 py-8 border-t border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">How PilatesPlanner Works</h3>
          <div className="flex flex-col md:flex-row justify-center items-center md:items-start space-y-6 md:space-y-0">
            <div className="flex flex-col items-center px-4 md:border-r md:border-gray-300">
              <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-lg font-semibold mb-2">1</div>
              <h4 className="font-medium text-gray-800 mb-1">Select Preferences</h4>
              <p className="text-gray-600 text-sm text-center max-w-[200px]">Choose equipment, target areas, and health considerations</p>
            </div>
            <div className="flex flex-col items-center px-4 md:border-r md:border-gray-300">
              <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-lg font-semibold mb-2">2</div>
              <h4 className="font-medium text-gray-800 mb-1">Generate Workout</h4>
              <p className="text-gray-600 text-sm text-center max-w-[200px]">Get a personalized workout based on your inputs</p>
            </div>
            <div className="flex flex-col items-center px-4">
              <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-lg font-semibold mb-2">3</div>
              <h4 className="font-medium text-gray-800 mb-1">Track Progress</h4>
              <p className="text-gray-600 text-sm text-center max-w-[200px]">Save workouts, track history, and reach your goals</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeOverview;